import React, { useEffect, useId, useMemo, useState, useRef } from "react";

import ReactQuill, { Quill } from 'react-quill';

import GalleryModal from "../Gallery/GalleryModal";
const BlockEmbed = Quill.import("blots/block/embed");
const Delta = Quill.import("delta");

const sanitizeTable = (sourceTable) => {
  const table = sourceTable.cloneNode(true);
  const allowedTags = new Set([
    "B", "BR", "CAPTION", "COL", "COLGROUP", "EM", "I", "P", "SPAN", "STRONG",
    "TBODY", "TD", "TFOOT", "TH", "THEAD", "TR", "U"
  ]);
  const allowedAttributes = new Set(["align", "colspan", "headers", "rowspan", "scope"]);

  [...table.querySelectorAll("*")].forEach((element) => {
    if (!allowedTags.has(element.tagName)) {
      element.replaceWith(...element.childNodes);
      return;
    }
    [...element.attributes].forEach((attribute) => {
      if (!allowedAttributes.has(attribute.name.toLowerCase())) element.removeAttribute(attribute.name);
    });
  });

  return table.innerHTML;
};

class PastedTableBlot extends BlockEmbed {
  static create(value) {
    const node = super.create();
    node.setAttribute("contenteditable", "false");
    node.setAttribute("aria-label", "Pasted table");
    node.innerHTML = typeof value === "string" ? value : "";
    return node;
  }

  static value(node) {
    return node.innerHTML;
  }
}

PastedTableBlot.blotName = "pastedTable";
PastedTableBlot.tagName = "TABLE";
PastedTableBlot.className = "ql-pasted-table";
Quill.register(PastedTableBlot);

const preservePastedTable = (node) => new Delta().insert({
  pastedTable: sanitizeTable(node)
});

const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "color",
  "background",
  "align",
  "list",
  "bullet",
  "indent",
  "link",
  "image",
  "video",
  "pastedTable"
];
// import axios from 'axios';
// imagePicker: Upload, media, normal
const ImageEditor = (props) => {
  const [openingModal, setOpeningModal] = useState(false);
  const [fixedToolbar, setFixedToolbar] = useState(null);
  const reactQuillRef = useRef();
  const composerRef = useRef();
  const toolbarRef = useRef();
  const toolbarAnchorRef = useRef();
  const toolbarId = `rich-text-toolbar-${useId().replace(/:/g, "")}`;
  const modules = useMemo(() => ({
    toolbar: { container: `#${toolbarId}` },
    clipboard: { matchers: [["TABLE", preservePastedTable]] },
  }), [toolbarId]);
  useEffect(() => {
    let animationFrame;
    const updateToolbarPosition = () => {
      cancelAnimationFrame(animationFrame);
      animationFrame = requestAnimationFrame(() => {
        const composer = composerRef.current;
        const toolbar = toolbarRef.current;
        const anchor = toolbarAnchorRef.current;
        if (!composer || !toolbar || !anchor) return;

        const topbarOffset = 56;
        const composerRect = composer.getBoundingClientRect();
        const anchorRect = anchor.getBoundingClientRect();
        const toolbarHeight = toolbar.offsetHeight;
        const shouldFix = anchorRect.top <= topbarOffset;

        setFixedToolbar((current) => {
          if (!shouldFix) return current === null ? current : null;
          const next = {
            left: composerRect.left,
            width: composerRect.width,
            height: toolbarHeight,
          };
          if (current
            && Math.abs(current.left - next.left) < 0.5
            && Math.abs(current.width - next.width) < 0.5
            && current.height === next.height) return current;
          return next;
        });
      });
    };

    updateToolbarPosition();
    window.addEventListener("scroll", updateToolbarPosition, { passive: true });
    window.addEventListener("resize", updateToolbarPosition);
    const resizeObserver = new ResizeObserver(updateToolbarPosition);
    if (composerRef.current) resizeObserver.observe(composerRef.current);
    if (toolbarRef.current) resizeObserver.observe(toolbarRef.current);

    return () => {
      cancelAnimationFrame(animationFrame);
      window.removeEventListener("scroll", updateToolbarPosition);
      window.removeEventListener("resize", updateToolbarPosition);
      resizeObserver.disconnect();
    };
  }, []);
  const { imagePicker = 'upload', onChange, className = '', ...restProps } = props;
  const handleSelect = (images) => {
    const quillRef = reactQuillRef.current.getEditor();
    const range = quillRef.getSelection(true);
    const position = range ? range.index : 0;
    const imageUrl = images ? images[0]?.image_url : null;

    if (imageUrl) {
      quillRef.editor.insertEmbed(position, 'image', imageUrl);
      onChange(quillRef.root.innerHTML);
    }
    setOpeningModal(false);

  }
  const handleChange = (value) => {
    onChange(value)
  }
  const imageHandler = (e) => {
    setOpeningModal(true);

    return false;

  };


  return (
    <div ref={composerRef} className={`rich-text-composer ${className}`.trim()}>
      <div ref={toolbarAnchorRef} className="rich-text-composer__toolbar-anchor" aria-hidden="true" />
      {fixedToolbar && <div className="rich-text-composer__toolbar-placeholder" style={{ height: fixedToolbar.height }} aria-hidden="true" />}
      <div
        ref={toolbarRef}
        className={`rich-text-composer__sticky-tools${fixedToolbar ? " is-fixed" : ""}`}
        style={fixedToolbar ? { left: fixedToolbar.left, width: fixedToolbar.width } : undefined}
      >
        <div className="rich-text-composer__actions">
          <button type="button" className="btn btn-green" onClick={imageHandler}>
            Insert image from media library
          </button>
          <span>Use headings to structure the article. Tables pasted from Word are preserved as complete blocks.</span>
        </div>
        <div id={toolbarId} className="ql-toolbar ql-snow" aria-label="Text formatting toolbar">
          <span className="ql-formats">
            <select className="ql-header" defaultValue="">
              <option value="1">Heading 1</option><option value="2">Heading 2</option>
              <option value="3">Heading 3</option><option value="4">Heading 4</option>
              <option value="5">Heading 5</option><option value="6">Heading 6</option>
              <option value="">Normal</option>
            </select>
          </span>
          <span className="ql-formats">
            <button type="button" className="ql-bold" /><button type="button" className="ql-italic" />
            <button type="button" className="ql-underline" /><button type="button" className="ql-strike" />
            <button type="button" className="ql-blockquote" />
          </span>
          <span className="ql-formats">
            <select className="ql-color" /><select className="ql-background" />
            <select className="ql-align" />
          </span>
          <span className="ql-formats">
            <button type="button" className="ql-list" value="ordered" />
            <button type="button" className="ql-list" value="bullet" />
            <button type="button" className="ql-indent" value="-1" />
            <button type="button" className="ql-indent" value="+1" />
          </span>
          <span className="ql-formats">
            <button type="button" className="ql-link" /><button type="button" className="ql-image" />
            <button type="button" className="ql-video" /><button type="button" className="ql-clean" />
          </span>
        </div>
      </div>
      <ReactQuill
        {...restProps}
        onChange={handleChange}
        ref={reactQuillRef}
        modules={modules}
        theme="snow"
        formats={quillFormats}
      />
      <GalleryModal
        maxNumberOfFiles={1}
        isOpen={!!openingModal}
        onCancel={(e) => setOpeningModal(null)}
        onSelectPhoto={handleSelect}
      />
    </div>
  );
};

export default ImageEditor;
